import { CircleAlert, CircleCheck, CircleX } from "lucide-react";
import { useEffect, useState } from "react";

import Loader from "../../../components/Loader";
import { Button } from "../../../components/ui/button";
import { base64ToAscii } from "../../../handlers/valueTransformation";
import { ResumeDataService } from "../../../services/create-resume/resume-data";

interface PaymentCompleteProps {
	orderID: string;
	loading: boolean;
	setLoading: (loading: boolean) => void;
	orderData: { orderID: string; paymentLink: string } | null;
	setOrderData: (data: { orderID: string; paymentLink: string }) => void;
	setCurrentStep: (step: number) => void;
}

function PaymentComplete({
	orderID,
	loading,
	setLoading,
	orderData,
	setOrderData,
	setCurrentStep,
}: PaymentCompleteProps) {
	const resumeDataService = new ResumeDataService();

	const [paymentStatus, setPaymentStatus] = useState("");
	const [errors, setErrors] = useState<string[]>([]);

	function onSuccess(response: { data: { paymentStatus: string; paymentLink: string; _id: string } }) {
		setLoading(false);
		const { paymentStatus, paymentLink, _id } = response.data;

		setOrderData({
			orderID: _id,
			paymentLink,
		});

		if (paymentStatus === "COMPLETED") {
			setCurrentStep(5);
			return setPaymentStatus(paymentStatus);
		}
		if (paymentStatus === "PENDING" || paymentStatus === "INPROGRESS") {
			return setPaymentStatus("PENDING");
		}
		if (paymentStatus === "CANCELLED" || paymentStatus === "REJECTED" || paymentStatus === "USERCANCELLED") {
			return setPaymentStatus("CANCELLED");
		}
	}

	function onError(error: { message: string }) {
		setLoading(false);
		setErrors([error.message]);
	}

	async function getPaymentStatus() {
		setLoading(true);
		await resumeDataService.getOrderStatus(orderID, onSuccess, onError);
	}

	useEffect(() => {
		getPaymentStatus();
	}, []);

	if (loading) {
		return (
			<div className="py-12 text-center">
				<Loader />
				<p className="mt-4 text-gray-600">Please wait while we are verifying your payment</p>
			</div>
		);
	}

	if (errors.length > 0) {
		return (
			<div className="py-12 text-center">
				<CircleX className="mx-auto h-16 w-16 text-red-500" />
				<div className="mx-auto mt-6 max-w-md rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
					<ul className="list-inside list-disc space-y-1">
						{errors.map((error, index) => (
							<li key={index}>{error}</li>
						))}
					</ul>
				</div>
				<div className="mt-6 flex justify-end">
					<Button
						onClick={() => {
							localStorage.removeItem("orderID");
							window.location.href = "/";
						}}
					>
						Go to Home
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div>
			{paymentStatus === "COMPLETED" && (
				<div className="py-12 text-center">
					<div className="flex min-h-[250px] flex-col items-center justify-center">
						<CircleCheck className="h-16 w-16 text-green-600" />
						<p className="mt-4 text-gray-700">Your payment has been successfully completed.</p>
					</div>
					<div className="flex justify-end">
						<Button
							onClick={() => {
								localStorage.removeItem("orderID");
								window.location.href = "/my-resumes";
							}}
						>
							Go to My Resumes
						</Button>
					</div>
				</div>
			)}
			{paymentStatus === "PENDING" && (
				<div className="py-12 text-center">
					<div className="flex min-h-[250px] flex-col items-center justify-center">
						<CircleAlert className="h-16 w-16 text-yellow-500" />
						<p className="mt-4 text-gray-700">
							Your payment is pending. Please complete your payment to access all features
						</p>
					</div>
					<div className="flex justify-end">
						<Button asChild>
							<a href={base64ToAscii(orderData?.paymentLink ?? "")} target="_blank" rel="noopener noreferrer">
								Make Payment
							</a>
						</Button>
					</div>
				</div>
			)}
			{paymentStatus === "CANCELLED" && (
				<div className="py-12 text-center">
					<div className="flex min-h-[250px] flex-col items-center justify-center">
						<CircleX className="h-16 w-16 text-red-500" />
						<p className="mt-4 text-gray-700">
							Your payment has been canceled. Please complete your payment to access all features
						</p>
					</div>
					<div className="flex justify-end gap-2">
						<Button
							variant="outline"
							onClick={() => {
								localStorage.removeItem("orderID");
								window.location.href = "/my-resumes";
							}}
						>
							Cancel
						</Button>
						<Button
							onClick={() => {
								localStorage.removeItem("orderID");
								window.location.href = "/payments";
							}}
						>
							Make Payment
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}

export default PaymentComplete;
